import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { PURCHASE_ORDER_STATUSES } from "@/server/platform/db/schema/purchase-orders";
import { LazyPage } from "@/shared/ui/LazyPage";
import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);
const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((module) => ({
    default: module.ErrorBoundary,
  })),
);
const AuthLayout = lazy(() =>
  import("./layout/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);

const LoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const TwoFactorVerifyPage = lazy(() =>
  import("@/modules/auth/presentation/pages/TwoFactorVerifyPage").then(
    (module) => ({
      default: module.TwoFactorVerifyPage,
    }),
  ),
);
const AuthenticatedLayout = lazy(() =>
  import("./layout/AuthenticatedLayout").then((module) => ({
    default: module.AuthenticatedLayout,
  })),
);
const DashboardPage = lazy(() =>
  import("@/modules/dashboard/presentation/pages/DashboardPage").then(
    (module) => ({
      default: module.DashboardPage,
    }),
  ),
);
const RolesPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RolesPage").then((module) => ({
    default: module.RolesPage,
  })),
);
const RoleCreatePage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleCreatePage").then(
    (module) => ({
      default: module.RoleCreatePage,
    }),
  ),
);
const RoleEditPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleEditPage").then((module) => ({
    default: module.RoleEditPage,
  })),
);
const BackupPage = lazy(() =>
  import("@/modules/backups/presentation/pages/BackupPage").then((module) => ({
    default: module.BackupPage,
  })),
);
const AuditPage = lazy(() =>
  import("@/modules/audit/presentation/pages/AuditPage").then((module) => ({
    default: module.AuditPage,
  })),
);
const AuditDetailPage = lazy(() =>
  import("@/modules/audit/presentation/pages/AuditDetailPage").then(
    (module) => ({
      default: module.AuditDetailPage,
    }),
  ),
);
const UsersPage = lazy(() =>
  import("@/modules/users/presentation/pages/UsersPage").then((module) => ({
    default: module.UsersPage,
  })),
);
const UserCreatePage = lazy(() =>
  import("@/modules/users/presentation/pages/UserCreatePage").then(
    (module) => ({
      default: module.UserCreatePage,
    }),
  ),
);
const UserEditPage = lazy(() =>
  import("@/modules/users/presentation/pages/UserEditPage").then((module) => ({
    default: module.UserEditPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/modules/auth/presentation/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const BranchesPage = lazy(() =>
  import("@/modules/branches/presentation/pages/BranchesPage").then((module) => ({
    default: module.BranchesPage,
  })),
);
const BranchesMapPage = lazy(() =>
  import("@/modules/branches/presentation/pages/BranchesMapPage").then(
    (module) => ({ default: module.BranchesMapPage }),
  ),
);
const BranchesCreatePage = lazy(() =>
  import("@/modules/branches/presentation/pages/BranchesCreatePage").then(
    (module) => ({ default: module.BranchesCreatePage }),
  ),
);
const BranchesEditPage = lazy(() =>
  import("@/modules/branches/presentation/pages/BranchesEditPage").then(
    (module) => ({ default: module.BranchesEditPage }),
  ),
);
const BranchSchedulePage = lazy(() =>
  import("@/modules/branches/presentation/pages/BranchSchedulePage").then(
    (module) => ({ default: module.BranchSchedulePage }),
  ),
);
const BranchPricingPage = lazy(() =>
  import("@/modules/pricing/presentation/pages/BranchPricingPage").then(
    (module) => ({ default: module.BranchPricingPage }),
  ),
);
const ComparePriceByBranchPage = lazy(() =>
  import(
    "@/modules/pricing/presentation/pages/ComparePriceByBranchPage"
  ).then((module) => ({
    default: module.ComparePriceByBranchPage,
  })),
);
const ProductsPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/ProductsPage").then((module) => ({
    default: module.ProductsPage,
  })),
);
const ProductCreatePage = lazy(() =>
  import("@/modules/catalog/presentation/pages/ProductCreatePage").then(
    (module) => ({ default: module.ProductCreatePage }),
  ),
);
const ProductEditPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/ProductEditPage").then(
    (module) => ({ default: module.ProductEditPage }),
  ),
);
const Forbidden = lazy(() =>
  import("./error/Forbidden").then((module) => ({
    default: module.Forbidden,
  })),
);

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
  beforeLoad: ({ location: { pathname } }) => {
    if (pathname === "/" || pathname === "/app") {
      throw redirect({ to: "/app/dashboard" });
    }
  },
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const twoFactorVerifyRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/two-factor",
  component: () => (
    <LazyPage>
      <TwoFactorVerifyPage />
    </LazyPage>
  ),
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: () => (
    <LazyPage>
      <AuthenticatedLayout />
    </LazyPage>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  validateSearch: (search: Record<string, unknown>) => {
    const preset = search.dateRangePreset as
      | "today"
      | "thisWeek"
      | "thisMonth"
      | "custom"
      | undefined;
    return {
      dateRangePreset:
        preset && ["today", "thisWeek", "thisMonth", "custom"].includes(preset)
          ? preset
          : "today",
      dateFrom: (search.dateFrom as string) || undefined,
      dateTo: (search.dateTo as string) || undefined,
      branchId: (search.branchId as string) || undefined,
    };
  },
  component: () => (
    <LazyPage>
      <DashboardPage />
    </LazyPage>
  ),
});

const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RolesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const systemBackupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/system/backup",
  component: () => (
    <RequirePermissions all={["system:backup"]}>
      <LazyPage>
        <BackupPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit",
  component: () => (
    <RequirePermissions all={["audit:read"]}>
      <LazyPage>
        <AuditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const auditDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit/$id",
  component: () => (
    <RequirePermissions all={["audit:read"]}>
      <LazyPage>
        <AuditDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: () => (
    <RequirePermissions all={["users:read"]}>
      <LazyPage>
        <UsersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:create"]}>
      <LazyPage>
        <UserCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:update"]}>
      <LazyPage>
        <UserEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const branchesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/branches",
  component: () => (
    <RequirePermissions all={["branches:read"]}>
      <LazyPage>
        <BranchesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const branchesMapRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/branches/map",
  component: () => (
    <RequirePermissions all={["branches:read"]}>
      <LazyPage>
        <BranchesMapPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const branchesCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/branches/create",
  component: () => (
    <RequirePermissions all={["branches:read"]} any={["branches:create"]}>
      <LazyPage>
        <BranchesCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const branchesEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/branches/$id/edit",
  component: () => (
    <RequirePermissions all={["branches:read"]} any={["branches:update"]}>
      <LazyPage>
        <BranchesEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const branchesScheduleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/branches/$id/schedule",
  component: () => (
    <RequirePermissions all={["branches:read"]} any={["branches:update"]}>
      <LazyPage>
        <BranchSchedulePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const branchPricingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/pricing/branch-overrides",
  component: () => (
    <RequirePermissions all={["pricing:read"]}>
      <LazyPage>
        <BranchPricingPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const comparePriceByBranchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/pricing/compare",
  component: () => (
    <RequirePermissions all={["pricing:read"]}>
      <LazyPage>
        <ComparePriceByBranchPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/products",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <ProductsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const productsCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/products/create",
  component: () => (
    <RequirePermissions all={["catalog:read"]} any={["catalog:create"]}>
      <LazyPage>
        <ProductCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const productEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/products/$id/edit",
  component: () => (
    <RequirePermissions all={["catalog:read"]} any={["catalog:update"]}>
      <LazyPage>
        <ProductEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const ModifiersPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/ModifiersPage").then((module) => ({
    default: module.ModifiersPage,
  })),
);
const ModifierCreatePage = lazy(() =>
  import(
    "@/modules/catalog/presentation/pages/ModifierCreatePage"
  ).then((module) => ({
    default: module.ModifierCreatePage,
  })),
);
const ModifierEditPage = lazy(() =>
  import(
    "@/modules/catalog/presentation/pages/ModifierEditPage"
  ).then((module) => ({
    default: module.ModifierEditPage,
  })),
);
const IngredientsPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/IngredientsPage"
  ).then((module) => ({ default: module.IngredientsPage })),
);
const IngredientCreatePage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/IngredientCreatePage"
  ).then((module) => ({ default: module.IngredientCreatePage })),
);
const LotsPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/LotsPage").then((module) => ({
    default: module.LotsPage,
  })),
);
const LotMovementsPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/LotMovementsPage"
  ).then((module) => ({ default: module.LotMovementsPage })),
);
const GrnFormPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/GrnFormPage"
  ).then((module) => ({ default: module.GrnFormPage })),
);
const MovementsPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/MovementsPage"
  ).then((module) => ({ default: module.MovementsPage })),
);
const IngredientEditPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/IngredientEditPage"
  ).then((module) => ({ default: module.IngredientEditPage })),
);
const IngredientCategoriesPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/IngredientCategoriesPage"
  ).then((module) => ({ default: module.IngredientCategoriesPage })),
);
const IngredientCategoryCreatePage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/IngredientCategoryCreatePage"
  ).then((module) => ({ default: module.IngredientCategoryCreatePage })),
);
const IngredientCategoryEditPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/IngredientCategoryEditPage"
  ).then((module) => ({ default: module.IngredientCategoryEditPage })),
);
const RecipeEditPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/RecipeEditPage"
  ).then((module) => ({ default: module.RecipeEditPage })),
);
const TransfersPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/TransfersPage"
  ).then((module) => ({ default: module.TransfersPage })),
);
const TransferDetailPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/TransferDetailPage"
  ).then((module) => ({ default: module.TransferDetailPage })),
);
const TransferShipPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/TransferShipPage"
  ).then((module) => ({ default: module.TransferShipPage })),
);
const SuppliersPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/SuppliersPage").then(
    (module) => ({ default: module.SuppliersPage }),
  ),
);
const SupplierCreatePage = lazy(() =>
  import("@/modules/inventory/presentation/pages/SupplierCreatePage").then(
    (module) => ({ default: module.SupplierCreatePage }),
  ),
);
const SupplierEditPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/SupplierEditPage").then(
    (module) => ({ default: module.SupplierEditPage }),
  ),
);
const PurchaseOrdersPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/PurchaseOrdersPage").then(
    (module) => ({ default: module.PurchaseOrdersPage }),
  ),
);
const PurchaseOrderCreatePage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/PurchaseOrderCreatePage"
  ).then((module) => ({ default: module.PurchaseOrderCreatePage })),
);
const PurchaseOrderEditPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/PurchaseOrderEditPage").then(
    (module) => ({ default: module.PurchaseOrderEditPage }),
  ),
);
const PurchaseOrderDetailPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/PurchaseOrderDetailPage"
  ).then((module) => ({ default: module.PurchaseOrderDetailPage })),
);
const StockPoliciesPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/StockPoliciesPage").then(
    (module) => ({ default: module.StockPoliciesPage }),
  ),
);
const OrdersPage = lazy(() =>
  import("@/modules/orders/presentation/pages/OrdersPage").then((module) => ({
    default: module.OrdersPage,
  })),
);
const OrderDetailPage = lazy(() =>
  import("@/modules/orders/presentation/pages/OrderDetailPage").then(
    (module) => ({ default: module.OrderDetailPage }),
  ),
);
const CustomersPage = lazy(() =>
  import("@/modules/customers/presentation/pages/CustomersPage").then(
    (module) => ({ default: module.CustomersPage }),
  ),
);
const CustomerDetailPage = lazy(() =>
  import("@/modules/customers/presentation/pages/CustomerDetailPage").then(
    (module) => ({ default: module.CustomerDetailPage }),
  ),
);
const WalletAccountsPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/WalletAccountsPage").then(
    (module) => ({ default: module.WalletAccountsPage }),
  ),
);
const TopupQueuePage = lazy(() =>
  import("@/modules/wallet/presentation/pages/TopupQueuePage").then(
    (module) => ({ default: module.TopupQueuePage }),
  ),
);
const RefundQueuePage = lazy(() =>
  import("@/modules/wallet/presentation/pages/RefundQueuePage").then(
    (module) => ({ default: module.RefundQueuePage }),
  ),
);
const LedgerExplorerPage = lazy(() =>
  import(
    "@/modules/wallet/presentation/pages/LedgerExplorerPage"
  ).then((module) => ({ default: module.LedgerExplorerPage })),
);
const WalletLimitsPage = lazy(() =>
  import(
    "@/modules/wallet/presentation/pages/WalletLimitsPage"
  ).then((module) => ({ default: module.WalletLimitsPage })),
);
const LoyaltyRulesPage = lazy(() =>
  import(
    "@/modules/loyalty/presentation/pages/LoyaltyRulesPage"
  ).then((module) => ({ default: module.LoyaltyRulesPage })),
);
const MembershipSettingsPage = lazy(() =>
  import(
    "@/modules/membership/presentation/pages/MembershipSettingsPage"
  ).then((module) => ({ default: module.MembershipSettingsPage })),
);
const RewardsPage = lazy(() =>
  import(
    "@/modules/loyalty/presentation/pages/RewardsPage"
  ).then((module) => ({ default: module.RewardsPage })),
);
const PromotionsPage = lazy(() =>
  import(
    "@/modules/promotions/presentation/pages/PromotionsPage"
  ).then((module) => ({ default: module.PromotionsPage })),
);
const PromotionCreatePage = lazy(() =>
  import(
    "@/modules/promotions/presentation/pages/PromotionCreatePage"
  ).then((module) => ({ default: module.PromotionCreatePage })),
);
const PromotionEditPage = lazy(() =>
  import(
    "@/modules/promotions/presentation/pages/PromotionEditPage"
  ).then((module) => ({ default: module.PromotionEditPage })),
);
const CouponsPage = lazy(() =>
  import(
    "@/modules/promotions/presentation/pages/CouponsPage"
  ).then((module) => ({ default: module.CouponsPage })),
);
const SalesReportsPage = lazy(() =>
  import(
    "@/modules/reports/presentation/pages/SalesReportsPage"
  ).then((module) => ({ default: module.SalesReportsPage })),
);
const OperationsReportsPage = lazy(() =>
  import(
    "@/modules/reports/presentation/pages/OperationsReportsPage"
  ).then((module) => ({ default: module.OperationsReportsPage })),
);
const WalletReportsPage = lazy(() =>
  import(
    "@/modules/reports/presentation/pages/WalletReportsPage"
  ).then((module) => ({ default: module.WalletReportsPage })),
);
const InventoryReportsPage = lazy(() =>
  import(
    "@/modules/reports/presentation/pages/InventoryReportsPage"
  ).then((module) => ({ default: module.InventoryReportsPage })),
);
const TransferReportsPage = lazy(() =>
  import(
    "@/modules/reports/presentation/pages/TransferReportsPage"
  ).then((module) => ({ default: module.TransferReportsPage })),
);
const PurchaseOrderReportsPage = lazy(() =>
  import(
    "@/modules/reports/presentation/pages/PurchaseOrderReportsPage"
  ).then((module) => ({ default: module.PurchaseOrderReportsPage })),
);
const LowStockReportsPage = lazy(() =>
  import("@/modules/reports/presentation/pages/LowStockReportsPage").then(
    (module) => ({ default: module.LowStockReportsPage }),
  ),
);
const CouponCreatePage = lazy(() =>
  import(
    "@/modules/promotions/presentation/pages/CouponCreatePage"
  ).then((module) => ({ default: module.CouponCreatePage })),
);
const CouponEditPage = lazy(() =>
  import(
    "@/modules/promotions/presentation/pages/CouponEditPage"
  ).then((module) => ({ default: module.CouponEditPage })),
);
const AnnouncementsPage = lazy(() =>
  import(
    "@/modules/announcements/presentation/pages/AnnouncementsPage"
  ).then((module) => ({ default: module.AnnouncementsPage })),
);
const AnnouncementCreatePage = lazy(() =>
  import(
    "@/modules/announcements/presentation/pages/AnnouncementCreatePage"
  ).then((module) => ({ default: module.AnnouncementCreatePage })),
);
const AnnouncementEditPage = lazy(() =>
  import(
    "@/modules/announcements/presentation/pages/AnnouncementEditPage"
  ).then((module) => ({ default: module.AnnouncementEditPage })),
);
const modifiersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/modifiers",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <ModifiersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const modifiersCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/modifiers/create",
  component: () => (
    <RequirePermissions all={["catalog:read"]} any={["catalog:create"]}>
      <LazyPage>
        <ModifierCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const modifiersEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/modifiers/$id/edit",
  component: () => (
    <RequirePermissions all={["catalog:read"]} any={["catalog:update"]}>
      <LazyPage>
        <ModifierEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const ingredientsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/ingredients",
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <IngredientsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const ingredientsCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/ingredients/create",
  component: () => (
    <RequirePermissions all={["inventory:read"]} any={["inventory:create"]}>
      <LazyPage>
        <IngredientCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const ingredientsEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/ingredients/$id/edit",
  component: () => (
    <RequirePermissions all={["inventory:read"]} any={["inventory:update"]}>
      <LazyPage>
        <IngredientEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const ingredientCategoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/ingredient-categories",
  validateSearch: (search: Record<string, unknown>) => ({
    search: typeof search.search === "string" ? search.search : undefined,
    isActive: typeof search.isActive === "string" ? search.isActive : undefined,
    offset: typeof search.offset === "number" ? search.offset : undefined,
    limit: typeof search.limit === "number" ? search.limit : undefined,
    sortField:
      typeof search.sortField === "string" ? search.sortField : undefined,
    sortOrder:
      search.sortOrder === "asc" || search.sortOrder === "desc"
        ? search.sortOrder
        : undefined,
  }),
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <IngredientCategoriesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const ingredientCategoriesCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/ingredient-categories/create",
  component: () => (
    <RequirePermissions all={["inventory:read"]} any={["inventory:create"]}>
      <LazyPage>
        <IngredientCategoryCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const ingredientCategoriesEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/ingredient-categories/$id/edit",
  component: () => (
    <RequirePermissions all={["inventory:read"]} any={["inventory:update"]}>
      <LazyPage>
        <IngredientCategoryEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const recipeEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/recipes/$variantId/edit",
  component: () => (
    <RequirePermissions all={["inventory:read"]} any={["inventory:update"]}>
      <LazyPage>
        <RecipeEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const suppliersSearchValidate = (search: Record<string, unknown>) => {
  const offsetRaw = search.offset;
  const limitRaw = search.limit;
  return {
    search: typeof search.search === "string" ? search.search : undefined,
    isActive: typeof search.isActive === "string" ? search.isActive : undefined,
    sortField:
      typeof search.sortField === "string" ? search.sortField : undefined,
    sortOrder:
      search.sortOrder === "asc" || search.sortOrder === "desc"
        ? search.sortOrder
        : undefined,
    offset:
      typeof offsetRaw === "number"
        ? offsetRaw
        : parseInt(String(offsetRaw ?? "0"), 10) || 0,
    limit:
      typeof limitRaw === "number"
        ? limitRaw
        : parseInt(String(limitRaw ?? "20"), 10) || 20,
  };
};

const suppliersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/suppliers",
  validateSearch: suppliersSearchValidate,
  component: () => (
    <RequirePermissions all={["inventory:purchase:read"]}>
      <LazyPage>
        <SuppliersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const suppliersCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/suppliers/create",
  component: () => (
    <RequirePermissions
      all={["inventory:purchase:read"]}
      any={["inventory:purchase:manage"]}
    >
      <LazyPage>
        <SupplierCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const suppliersEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/suppliers/$id/edit",
  component: () => (
    <RequirePermissions
      all={["inventory:purchase:read"]}
      any={["inventory:purchase:manage"]}
    >
      <LazyPage>
        <SupplierEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const purchaseOrdersSearchValidate = (search: Record<string, unknown>) => {
  const status = search.status as string | undefined;
  const offsetRaw = search.offset;
  const limitRaw = search.limit;
  return {
    status:
      status &&
      PURCHASE_ORDER_STATUSES.includes(
        status as (typeof PURCHASE_ORDER_STATUSES)[number],
      )
        ? (status as (typeof PURCHASE_ORDER_STATUSES)[number])
        : undefined,
    supplierId: (search.supplierId as string) || undefined,
    orderDateFrom: (search.orderDateFrom as string) || undefined,
    orderDateTo: (search.orderDateTo as string) || undefined,
    poNo: (search.poNo as string) || undefined,
    offset:
      typeof offsetRaw === "number"
        ? offsetRaw
        : parseInt(String(offsetRaw ?? "0"), 10) || 0,
    limit:
      typeof limitRaw === "number"
        ? limitRaw
        : parseInt(String(limitRaw ?? "20"), 10) || 20,
  };
};

const stockPoliciesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/stock-policies",
  validateSearch: (search: Record<string, unknown>) => ({
    policyBranch:
      typeof search.policyBranch === "string" ? search.policyBranch : undefined,
    onHandBranchId:
      typeof search.onHandBranchId === "string"
        ? search.onHandBranchId
        : undefined,
    itemType: typeof search.itemType === "string" ? search.itemType : undefined,
    categoryId:
      typeof search.categoryId === "string" ? search.categoryId : undefined,
    search: typeof search.search === "string" ? search.search : undefined,
    belowMinOnly:
      typeof search.belowMinOnly === "string" ? search.belowMinOnly : undefined,
    offset:
      typeof search.offset === "number"
        ? search.offset
        : parseInt(String(search.offset ?? "0"), 10) || undefined,
    limit:
      typeof search.limit === "number"
        ? search.limit
        : parseInt(String(search.limit ?? "50"), 10) || undefined,
  }),
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <StockPoliciesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/purchase-orders",
  validateSearch: purchaseOrdersSearchValidate,
  component: () => (
    <RequirePermissions all={["inventory:purchase:read"]}>
      <LazyPage>
        <PurchaseOrdersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const purchaseOrdersCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/purchase-orders/create",
  component: () => (
    <RequirePermissions
      all={["inventory:purchase:read"]}
      any={["inventory:purchase:create"]}
    >
      <LazyPage>
        <PurchaseOrderCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const purchaseOrderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/purchase-orders/$id",
  component: () => (
    <RequirePermissions all={["inventory:purchase:read"]}>
      <LazyPage>
        <PurchaseOrderDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const purchaseOrderEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/purchase-orders/$id/edit",
  component: () => (
    <RequirePermissions
      all={["inventory:purchase:read"]}
      any={["inventory:purchase:create"]}
    >
      <LazyPage>
        <PurchaseOrderEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const lotsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/lots",
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <LotsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const lotsReceiveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/lots/receive",
  component: () => (
    <RequirePermissions all={["inventory:read"]} any={["inventory:update"]}>
      <LazyPage>
        <GrnFormPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const movementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/movements",
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <MovementsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const lotMovementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/lots/$id/movements",
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <LotMovementsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const TRANSFER_STATUSES = [
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "SHIPPED",
  "RECEIVED",
  "CLOSED",
  "REJECTED",
  "CANCELLED",
] as const;

const transferReportsSearchValidate = (search: Record<string, unknown>) => {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const pad = (n: number) => String(n).padStart(2, "0");
  const defaultFrom = `${monthStart.getFullYear()}-${pad(monthStart.getMonth() + 1)}-${pad(monthStart.getDate())}`;
  const defaultTo = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const dateFieldRaw = search.dateField as string | undefined;
  const dateFields = ["submitted", "shipped", "received", "closed"] as const;
  const groupByRaw = search.groupBy as string | undefined;
  const groupByOptions = ["day", "week", "month"] as const;
  const offsetRaw = search.offset;
  const limitRaw = search.limit;
  return {
    from: (search.from as string) || defaultFrom,
    to: (search.to as string) || defaultTo,
    dateField:
      dateFieldRaw && dateFields.includes(dateFieldRaw as (typeof dateFields)[number])
        ? (dateFieldRaw as (typeof dateFields)[number])
        : "submitted",
    groupBy:
      groupByRaw &&
      groupByOptions.includes(groupByRaw as (typeof groupByOptions)[number])
        ? (groupByRaw as (typeof groupByOptions)[number])
        : "day",
    fromBranchId: (search.fromBranchId as string) || undefined,
    ingredientCategoryId:
      (search.ingredientCategoryId as string) || undefined,
    offset:
      typeof offsetRaw === "number"
        ? offsetRaw
        : parseInt(String(offsetRaw ?? "0"), 10) || 0,
    limit:
      typeof limitRaw === "number"
        ? limitRaw
        : parseInt(String(limitRaw ?? "50"), 10) || 50,
  };
};

const transfersSearchValidate = (search: Record<string, unknown>) => {
  const status = search.status as string | undefined;
  const offsetRaw = search.offset;
  const limitRaw = search.limit;
  return {
    status:
      status &&
      TRANSFER_STATUSES.includes(status as (typeof TRANSFER_STATUSES)[number])
        ? (status as (typeof TRANSFER_STATUSES)[number])
        : undefined,
    fromBranchId: (search.fromBranchId as string) || undefined,
    toBranchId: (search.toBranchId as string) || undefined,
    submittedFrom: (search.submittedFrom as string) || undefined,
    submittedTo: (search.submittedTo as string) || undefined,
    offset:
      typeof offsetRaw === "number"
        ? offsetRaw
        : parseInt(String(offsetRaw ?? "0"), 10) || 0,
    limit:
      typeof limitRaw === "number"
        ? limitRaw
        : parseInt(String(limitRaw ?? "20"), 10) || 20,
  };
};

const transfersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/transfers",
  validateSearch: transfersSearchValidate,
  component: () => (
    <RequirePermissions all={["inventory:transfer:read"]}>
      <LazyPage>
        <TransfersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const transferDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/transfers/$id",
  component: () => (
    <RequirePermissions all={["inventory:transfer:read"]}>
      <LazyPage>
        <TransferDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const transferShipRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/transfers/$id/ship",
  component: () => (
    <RequirePermissions
      all={["inventory:transfer:read"]}
      any={["inventory:transfer:ship"]}
    >
      <LazyPage>
        <TransferShipPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const ordersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <OrdersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const orderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/$id",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <OrderDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers",
  validateSearch: (search: Record<string, unknown>) => {
    const sf = search.sortField as string | undefined;
    const so = search.sortOrder as string | undefined;
    const offsetRaw = search.offset;
    const limitRaw = search.limit;
    const kyc = search.kycStatus as string | undefined;
    return {
      q: (search.q as string) || undefined,
      kycStatus:
        kyc &&
        ["NONE", "PENDING", "VERIFIED", "REJECTED"].includes(kyc)
          ? (kyc as "NONE" | "PENDING" | "VERIFIED" | "REJECTED")
          : undefined,
      sortField:
        sf && ["createdAt", "name", "phone"].includes(sf)
          ? sf
          : "createdAt",
      sortOrder: so === "asc" || so === "desc" ? so : "desc",
      offset:
        typeof offsetRaw === "number"
          ? offsetRaw
          : parseInt(String(offsetRaw ?? "0"), 10) || 0,
      limit:
        typeof limitRaw === "number"
          ? limitRaw
          : parseInt(String(limitRaw ?? "20"), 10) || 20,
    };
  },
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <CustomersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers/$id",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <CustomerDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const walletAccountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/accounts",
  component: () => (
    <RequirePermissions all={["wallet:read"]}>
      <LazyPage>
        <WalletAccountsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const walletTopupQueueRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/topup-queue",
  component: () => (
    <RequirePermissions all={["wallet:read"]}>
      <LazyPage>
        <TopupQueuePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const walletRefundQueueRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/refund-queue",
  component: () => (
    <RequirePermissions all={["wallet:read"]}>
      <LazyPage>
        <RefundQueuePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const walletLedgerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/ledger",
  component: () => (
    <RequirePermissions all={["wallet:read"]}>
      <LazyPage>
        <LedgerExplorerPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const walletLimitsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/limits/$walletId",
  component: () => (
    <RequirePermissions all={["wallet:read"]} any={["wallet:update"]}>
      <LazyPage>
        <WalletLimitsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const loyaltyRulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/loyalty/settings",
  component: () => (
    <RequirePermissions all={["loyalty:read"]}>
      <LazyPage>
        <LoyaltyRulesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const membershipSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/membership/settings",
  component: () => (
    <RequirePermissions all={["membership:read"]}>
      <LazyPage>
        <MembershipSettingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const loyaltyRewardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/loyalty/rewards",
  component: () => (
    <RequirePermissions all={["loyalty:read"]}>
      <LazyPage>
        <RewardsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const loyaltyPromotionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/loyalty/promotions",
  component: () => (
    <RequirePermissions all={["loyalty:read"]}>
      <LazyPage>
        <PromotionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const loyaltyPromotionsCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/loyalty/promotions/create",
  component: () => (
    <RequirePermissions all={["loyalty:read"]} any={["loyalty:update"]}>
      <LazyPage>
        <PromotionCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const loyaltyPromotionsEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/loyalty/promotions/$promotionId/edit",
  component: () => (
    <RequirePermissions all={["loyalty:read"]} any={["loyalty:update"]}>
      <LazyPage>
        <PromotionEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const loyaltyCouponsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/loyalty/promotions/coupons",
  component: () => (
    <RequirePermissions all={["loyalty:read"]}>
      <LazyPage>
        <CouponsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const salesReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/sales",
  component: () => (
    <RequirePermissions all={["reports:sales:read"]}>
      <LazyPage>
        <SalesReportsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const operationsReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/operations",
  component: () => (
    <RequirePermissions all={["reports:ops:read"]}>
      <LazyPage>
        <OperationsReportsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const walletReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/wallet",
  component: () => (
    <RequirePermissions all={["reports:wallet:read"]}>
      <LazyPage>
        <WalletReportsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const inventoryReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/inventory",
  component: () => (
    <RequirePermissions all={["reports:inventory:read"]}>
      <LazyPage>
        <InventoryReportsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const transferReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/inventory/transfers",
  validateSearch: transferReportsSearchValidate,
  component: () => (
    <RequirePermissions all={["reports:inventory:read"]}>
      <LazyPage>
        <TransferReportsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const purchaseOrderReportsSearchValidate = (search: Record<string, unknown>) => ({
  orderDateFrom: (search.orderDateFrom as string) || undefined,
  orderDateTo: (search.orderDateTo as string) || undefined,
  supplierId: (search.supplierId as string) || undefined,
});

const purchaseOrderReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/inventory/purchase-orders",
  validateSearch: purchaseOrderReportsSearchValidate,
  component: () => (
    <RequirePermissions
      any={["reports:inventory:read", "inventory:purchase:read"]}
    >
      <LazyPage>
        <PurchaseOrderReportsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const lowStockReportsSearchValidate = (search: Record<string, unknown>) => ({
  branchId: (search.branchId as string) || undefined,
  itemType: (search.itemType as string) || undefined,
  categoryId: (search.categoryId as string) || undefined,
  offset:
    typeof search.offset === "number"
      ? search.offset
      : parseInt(String(search.offset ?? "0"), 10) || undefined,
  limit:
    typeof search.limit === "number"
      ? search.limit
      : parseInt(String(search.limit ?? "100"), 10) || undefined,
});

const lowStockReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/inventory/low-stock",
  validateSearch: lowStockReportsSearchValidate,
  component: () => (
    <RequirePermissions any={["reports:inventory:read", "inventory:read"]}>
      <LazyPage>
        <LowStockReportsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const loyaltyCouponsCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/loyalty/promotions/coupons/create",
  component: () => (
    <RequirePermissions all={["loyalty:read"]} any={["loyalty:update"]}>
      <LazyPage>
        <CouponCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const loyaltyCouponsEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/loyalty/promotions/coupons/$couponId/edit",
  component: () => (
    <RequirePermissions all={["loyalty:read"]} any={["loyalty:update"]}>
      <LazyPage>
        <CouponEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const announcementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/announcements",
  component: () => (
    <RequirePermissions all={["announcements:read"]}>
      <LazyPage>
        <AnnouncementsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const announcementsCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/announcements/create",
  component: () => (
    <RequirePermissions
      all={["announcements:read"]}
      any={["announcements:update"]}
    >
      <LazyPage>
        <AnnouncementCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const announcementsEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/announcements/$announcementId/edit",
  component: () => (
    <RequirePermissions
      all={["announcements:read"]}
      any={["announcements:update"]}
    >
      <LazyPage>
        <AnnouncementEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  validateSearch: (search: Record<string, unknown>) => {
    const tab = search.tab as string | undefined;
    const validTabs = ["profile", "security", "sessions"] as const;
    return {
      tab:
        tab && validTabs.includes(tab as (typeof validTabs)[number])
          ? (tab as (typeof validTabs)[number])
          : "profile",
    };
  },
  component: () => (
    <LazyPage>
      <ProfilePage />
    </LazyPage>
  ),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/errors/forbidden",
  component: () => (
    <LazyPage>
      <Forbidden />
    </LazyPage>
  ),
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute, twoFactorVerifyRoute]),
  appRoute.addChildren([
    dashboardRoute,
    rolesRoute,
    roleCreateRoute,
    roleEditRoute,
    branchesRoute,
    branchesMapRoute,
    branchesCreateRoute,
    branchesEditRoute,
    branchesScheduleRoute,
    branchPricingRoute,
    comparePriceByBranchRoute,
    productsRoute,
    productsCreateRoute,
    productEditRoute,
    modifiersRoute,
    modifiersCreateRoute,
    modifiersEditRoute,
    ingredientsRoute,
    ingredientsCreateRoute,
    ingredientsEditRoute,
    ingredientCategoriesRoute,
    ingredientCategoriesCreateRoute,
    ingredientCategoriesEditRoute,
    stockPoliciesRoute,
    recipeEditRoute,
    suppliersRoute,
    suppliersCreateRoute,
    suppliersEditRoute,
    purchaseOrdersRoute,
    purchaseOrdersCreateRoute,
    purchaseOrderDetailRoute,
    purchaseOrderEditRoute,
    lotsRoute,
    lotsReceiveRoute,
    movementsRoute,
    lotMovementsRoute,
    transfersRoute,
    transferDetailRoute,
    transferShipRoute,
    ordersRoute,
    orderDetailRoute,
    customersRoute,
    customerDetailRoute,
    walletAccountsRoute,
    walletTopupQueueRoute,
    walletRefundQueueRoute,
    walletLedgerRoute,
    walletLimitsRoute,
    loyaltyRulesRoute,
    membershipSettingsRoute,
    loyaltyRewardsRoute,
    loyaltyPromotionsRoute,
    loyaltyPromotionsCreateRoute,
    loyaltyPromotionsEditRoute,
    loyaltyCouponsRoute,
    loyaltyCouponsCreateRoute,
    loyaltyCouponsEditRoute,
    announcementsRoute,
    announcementsCreateRoute,
    announcementsEditRoute,
    salesReportsRoute,
    operationsReportsRoute,
    walletReportsRoute,
    inventoryReportsRoute,
    transferReportsRoute,
    purchaseOrderReportsRoute,
    lowStockReportsRoute,
    usersRoute,
    userCreateRoute,
    userEditRoute,
    systemBackupRoute,
    auditRoute,
    auditDetailRoute,
    profileRoute,
  ]),
  forbiddenRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
